import { IEventAggregator, resolve } from "aurelia";
import { I18N } from "@aurelia/i18n";
import { IRoute, IRouteableComponent, IRouter, RouterNavigationCancelEvent } from "@aurelia/router";
import { fromState } from "@aurelia/state";

import { toggleTheme } from "@/helpers/util";
import { IUiSettings } from "@/interfaces";
import { VISIBILITY_CHANGE } from "@/static/eventChannels";
import { IState } from "@/store/state";

export class AppMain implements IRouteableComponent {
  propagatePageVisibilityChange = () => {
    this.ea.publish(VISIBILITY_CHANGE, document.visibilityState == "visible");
  };

  @fromState((state: IState) => {
    return state.settings.isSidebarOpen;
  })
  isSidebarOpen: boolean;

  @fromState((state: IState) => state.isLoggedIn)
  isLoggedIn: boolean;

  @fromState((state: IState) => state.settings)
  settings: IUiSettings;

  private i18n: I18N = resolve(I18N);
  private router: IRouter = resolve(IRouter);
  private ea: IEventAggregator = resolve(IEventAggregator);

  bound() {
    this.i18n.setLocale(this.settings.language);
    this.ea.subscribe("au:router:navigation-cancel", (payload: RouterNavigationCancelEvent) => {
      this.router.load("login", {
        query: "returnTo=" + payload.navigation.instruction,
      });
    });
  }

  // Routes
  static routes: IRoute[] = [
    {
      id: "login",
      path: "login",
      component: import("../login/mt-login-page"),
      title: "Sign in",
    },
    {
      id: "user-settings",
      path: "user",
      component: import("../settings/sections/mt-user-settings"),
      title: "User Information",
      parameters: {
        isProfileOnly: true,
      },
    },
    {
      id: "trends",
      path: "trends",
      component: import("../trends/mt-custom-trends"),
      title: "Trends",
      reloadBehavior: "refresh",
    },
    {
      id: "alarms",
      path: "alarms",
      component: import("../alarms/mt-alarms-page"),
      title: "Alarms and Events",
      reloadBehavior: "refresh",
    },
    {
      id: "cctv",
      path: "cctv/:vesselId?",
      component: import("../cctv/mt-cctv-page"),
      title: "CCTV",
      reloadBehavior: "refresh",
    },
    {
      id: "cobras",
      path: "cobras",
      component: import("../cobras/mt-cobras-page"),
      title: "COBRAS",
      reloadBehavior: "refresh",
    },
    {
      id: "dhub",
      path: "dhub",
      component: import("../dhub/mt-dhub-page"),
      title: "Data Hub",
      reloadBehavior: "refresh",
    },
    {
      id: "dp",
      path: "dp",
      component: import("../dp/mt-dp-page"),
      title: "DP",
      reloadBehavior: "refresh",
    },
    {
      id: "fuel",
      path: "fuel",
      component: import("../fuel/mt-fuel-page"),
      title: "Fuel",
      reloadBehavior: "refresh",
    },
    {
      id: "locator",
      path: "locator",
      component: import("../locator/mt-locator-page"),
      title: "Locator",
      reloadBehavior: "refresh",
    },
    {
      id: "landing",
      path: "landing",
      component: import("../landing/mt-landing"),
      title: "Landing",
    },
    {
      id: "pb-reporting",
      path: "pb-reporting",
      component: import("../pb-reporting/mt-pb-reporting"),
      title: "Petrobras Reporting",
      reloadBehavior: "refresh",
    },
    {
      id: "create-report",
      path: "create-report",
      component: import("../pb-reporting/create-report/mt-create-pb-report"),
      title: "Create Report",
    },
    {
      id: "edit-report",
      path: "edit-report/:reportId",
      component: import("../pb-reporting/create-report/mt-create-pb-report"),
      title: "Edit Report",
    },
    {
      id: "playback",
      path: "playback",
      component: import("../playback/mt-playback"),
      title: "Playback",
      reloadBehavior: "refresh",
    },
    // {
    //   id: "advanced",
    //   path: "advanced",
    //   component: import("../advanced-view/mt-advanced-view"),
    //   title: "Advanced Data Hub",
    // },
    {
      id: "cam",
      path: "cam",
      component: import("../cam/mt-cam"),
      title: "CAM",
    },
    {
      id: "easog",
      path: "easog",
      component: import("../easog/mt-easog"),
      title: "eASOG",
    },
    {
      id: "ndm",
      path: "ndm",
      component: import("../ndm/mt-ndm"),
      title: "NDM",
    },
    {
      id: "settings",
      path: "settings",
      component: import("../settings/mt-settings"),
      title: "Settings",
      reloadBehavior: "refresh",
    },
    {
      id: "help",
      path: "help",
      component: import("../help/mt-help"),
      title: "Help & Support",
    },
    {
      id: "pb-start",
      path: "pb-start",
      component: import("../pb-start/mt-pb-start"),
      title: "Petrobras Start",
    },
    {
      id: "start",
      path: "start",
      component: import("../dashboards/start/mt-start-page"),
      title: "Start Page",
    },
    {
      id: "vessel-info",
      path: "vessel",
      component: import("../vessel-info/mt-vessel-info"),
      title: "Vessel Info",
      reloadBehavior: "refresh",
    },
    // {
    //   id: "helm",
    //   path: "helm",
    //   component: import("../helm/mt-helm"),
    //   title: "HELM",
    //   reloadBehavior: "refresh",
    // },
    {
      id: "dashboards",
      path: "dashboards",
      component: import("../dashboards/mt-dashboards"),
      title: "Dashboards",
      reloadBehavior: "refresh",
    },
    {
      id: "adv-dashboards",
      path: "dashboards/:vesselId/:configId",
      component: import("../advanced-view/mt-advanced-view"),
      title: "Dashboards",
    },
    {
      id: "events",
      path: "events",
      component: import("../events/mt-event-log"),
      title: "Events Log",
      reloadBehavior: "refresh",
    },
    {
      id: "fleet-management",
      path: "fleet-management",
      component: import("../fleet-management/mt-fleet-management"),
      title: "Fleet Management",
    },
    {
      id: "backoffice",
      path: "backoffice",
      component: import("../backoffice/mt-backoffice"),
      title: "Backoffice",
    },
    {
      id: "access-control",
      path: "access-control",
      component: import("../access-control/mt-access-control"),
      title: "Access Control",
    },
    {
      id: "fall",
      path: ["/*", ""],
      redirectTo: "landing",
    },
  ];

  attached() {
    if (this.settings.isDarkThemeOn) {
      toggleTheme();
    }
    document.addEventListener(VISIBILITY_CHANGE, this.propagatePageVisibilityChange);
  }
}
